import React, { memo, useCallback, useEffect, useState } from "react";
import { placeholder, XMarkIcon } from "@";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

interface MediaItem {
  url: string;
  type: number; // 1 = image, 2 = video
}

interface MediaSliderProps {
  items: MediaItem[];
  initialIndex?: number;
  visible: boolean;
  onClose: () => void;
}

const MediaSlider = ({
  items,
  initialIndex = 0,
  visible,
  onClose,
}: MediaSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      setIsLoading(true);
    }
  }, [visible, initialIndex]);

  const goNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsLoading(true);
      setCurrentIndex((prev) => (prev + 1) % items.length);
    },
    [items.length],
  );

  const goPrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsLoading(true);
      setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    },
    [items.length],
  );

  const goToIndex = useCallback((e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setIsLoading(true);
    setCurrentIndex(index);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      // RTL: ArrowRight = prev, ArrowLeft = next
      if (e.key === "ArrowRight") {
        setIsLoading(true);
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
      }
      if (e.key === "ArrowLeft") {
        setIsLoading(true);
        setCurrentIndex((prev) => (prev + 1) % items.length);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, items.length, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  if (!visible || items.length === 0) return null;

  const currentItem = items[currentIndex];
  const isImage = currentItem.type === 1;
  const hasMultiple = items.length > 1;

  const navBtn =
    "group flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-white/20 active:scale-95";

  return (
    <div className="fixed inset-0 z-[999] flex flex-col items-center justify-center bg-black/85 backdrop-blur-md">
      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-5 py-4">
        {/* Counter chip */}
        {hasMultiple ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-md">
            <span className="font-mono tabular-nums">
              {currentIndex + 1}
            </span>
            <span className="text-white/40">/</span>
            <span className="font-mono tabular-nums text-white/70">
              {items.length}
            </span>
          </span>
        ) : (
          <span />
        )}

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="إغلاق"
          className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition-all duration-200 hover:rotate-90 hover:bg-white/20 active:scale-95"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Media content */}
      <div className="flex items-center justify-center gap-4 md:gap-6">
        {hasMultiple && (
          <button
            onClick={goNext}
            aria-label="التالي"
            className={navBtn}
          >
            <ChevronRightIcon className="h-6 w-6 transition-transform group-hover:-translate-x-0.5" />
          </button>
        )}

        <div
          onClick={(e) => e.stopPropagation()}
          className="relative flex h-full w-fit max-w-5xl cursor-default items-center justify-center md:h-[700px] lg:h-[800px]"
        >
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-[3px] border-white/20 border-t-white" />
            </div>
          )}
          {isImage ? (
            <img
              key={currentItem.url}
              alt=""
              loading="lazy"
              src={currentItem.url || placeholder}
              onLoad={() => setIsLoading(false)}
              className={`mx-auto max-h-full max-w-full rounded-2xl ring-1 ring-white/10 transition-opacity duration-300 ${
                isLoading ? "opacity-0" : "opacity-100"
              }`}
            />
          ) : (
            <video
              key={currentItem.url}
              className={`mx-auto max-h-full max-w-full rounded-2xl ring-1 ring-white/10 transition-opacity duration-300 ${
                isLoading ? "opacity-0" : "opacity-100"
              }`}
              autoPlay
              muted
              controls
              onLoadedData={() => setIsLoading(false)}
            >
              <source src={currentItem.url || placeholder} type="video/mp4" />
              <source
                src={currentItem.url || placeholder}
                type="video/quicktime"
              />
            </video>
          )}
        </div>

        {hasMultiple && (
          <button
            onClick={goPrev}
            aria-label="السابق"
            className={navBtn}
          >
            <ChevronLeftIcon className="h-6 w-6 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
      </div>

      {/* Dot indicators */}
      {hasMultiple && (
        <div className="absolute bottom-6 flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-2 backdrop-blur-md">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={(e) => goToIndex(e, index)}
              aria-label={`الذهاب إلى ${index + 1}`}
              className={`rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "h-2 w-6 bg-white"
                  : "h-2 w-2 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(MediaSlider);
